import { useState, useRef, useEffect } from 'react';
import { Check, Sparkles } from 'lucide-react';
import beeIcon from '../../assets/icons/assistant-bee.png';
import owlIcon from '../../assets/icons/assistant-owl.png';
import squirrelIcon from '../../assets/icons/assistant-squirrel.png';

export type AIModel = 'norm' | 'exp' | 'eco';

interface ModelPersona {
  id: AIModel;
  name: string;
  modelName: string;
  description: string;
  icon: string;
}

interface ModelSelectorProps {
  selectedModel: AIModel;
  onModelChange: (model: AIModel) => void;
}

const MODEL_PERSONAS: ModelPersona[] = [
  {
    id: 'norm',
    name: 'Bee',
    modelName: 'Claude Haiku 4.5',
    description: "Balanced, good problem solving and usage",
    icon: beeIcon
  },
  {
    id: 'exp',
    name: 'Owl',
    modelName: 'Claude Sonnet 4.5',
    description: "Heavy usage, very good problem solving",
    icon: owlIcon
  },
  {
    id: 'eco',
    name: 'Squirrel',
    modelName: 'Grok Fast',
    description: "Small usage, weaker problem solving",
    icon: squirrelIcon
  }
];

export default function ModelSelector({ 
  selectedModel, 
  onModelChange
}: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedPersona = MODEL_PERSONAS.find(p => p.id === selectedModel) || MODEL_PERSONAS[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (model: AIModel) => {
    onModelChange(model);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-300 dark:border-dark-border"
        title={`Model: ${selectedPersona.name}`}
      >
        <Sparkles className="w-5 h-5 text-gray-600 dark:text-dark-text-muted" />
        <img src={selectedPersona.icon} alt={selectedPersona.name} className="w-6 h-6 object-contain rounded-full" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-300 dark:border-dark-border rounded-lg shadow-xl z-50 overflow-hidden">
          {MODEL_PERSONAS.map((persona) => (
            <button
              key={persona.id}
              onClick={() => handleSelect(persona.id)}
              className={`w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-dark-border last:border-b-0 ${
                selectedModel === persona.id ? 'bg-gray-50 dark:bg-gray-700/50' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <img src={persona.icon} alt={persona.name} className="w-8 h-8 object-contain flex-shrink-0 rounded-full" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900 dark:text-dark-text">{persona.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-500">({persona.modelName})</span>
                    {selectedModel === persona.id && (
                      <Check className="w-4 h-4 text-green-500" strokeWidth={2.5} />
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-dark-text-muted leading-relaxed">
                    {persona.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

